import { ball, body, coverBg, edge, fade, gloss, mix, n, rng, shade, star, tint, type ArtPal } from './kit';

/**
 * Pirate Cove: a lever machine standing on a wooden pier of a friendly cove (the left third stays calm for labels, every
 * focus object sits right of x 213) — a cabinet of warm ship planks with brass corners and rivets, a rope along its edge,
 * a ship's wheel on the crest between two ship lanterns, five reels (shell · map · chest · map · anchor), a lever with a
 * turned wooden handle on its right. Behind: sea, a far lighthouse on a rock and a sail; evening with a moon in dark,
 * a sunny bay with gulls in light. No casino props, no skulls, no weapons.
 */
export function pirateCoveCover(P: ArtPal, p: string): string {
  const top = P.dark ? mix(P.bg, P.blue, 0.28) : mix(P.sky, '#FFFFFF', 0.72);
  const mid = P.dark ? mix(P.panel, P.purple, 0.16) : mix(P.sky, '#FFFFFF', 0.86);
  const bottom = P.dark ? mix(P.bg, P.teal, 0.14) : mix(P.teal, '#FFFFFF', 0.8);
  const bg = coverBg(p, P, top, mid, bottom, [430, 190]);
  const gold = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.72);
  const wood = shade(mix(P.orange, P.gold, 0.3), P.dark ? 0.42 : 0.3);
  const plank = shade(wood, 0.35);
  const sea = P.dark ? mix(P.bg, P.teal, 0.3) : mix(P.teal, P.sky, 0.35);
  const rope = P.dark ? '#E6CB94' : '#D9B26A';
  const cream = P.dark ? '#F3E9D2' : '#FFF8E8';
  const lamp = P.dark ? '#FFE9A8' : '#FFE7A3';
  const ol = (c: string, w = 2) => `stroke="${edge(c, P)}" stroke-width="${w}" stroke-linejoin="round"`;
  const g = (x: number, y: number, s: number, inner: string) =>
    `<g transform="translate(${x} ${y}) scale(${s})">${inner}</g>`;

  /* mini reel symbols, centred on (0, 0), ~34 px */
  const chest =
    `<ellipse cx="0" cy="-6" rx="17" ry="10" fill="url(#${p}lh)"/>` +
    `<path d="M-13 0L-12 -6L12 -11L13 0Z" fill="${lamp}"/>` +
    `<circle cx="-6" cy="-3" r="3" fill="${cream}"/><path d="M4 -9L8 -5L4 -1L0 -5Z" fill="${P.red}"/>` +
    `<rect x="-14" y="0" width="28" height="16" rx="2" fill="url(#${p}wd)" ${ol(wood, 1.4)}/>` +
    `<path d="M-14 -4C-14 -14 -6 -18 0 -18C10 -18 14 -14 14 -6Z" transform="rotate(-14 -14 -4)" fill="url(#${p}wd)" ${ol(wood, 1.4)}/>` +
    `<rect x="-15" y="0" width="30" height="3.5" fill="url(#${p}gd)"/><rect x="-3" y="2" width="6" height="7" rx="1.5" fill="url(#${p}gd)" ${ol(gold, 1)}/>`;
  const map =
    `<path d="M-12 -12Q0 -9 12 -12V12Q0 9 -12 12Z" fill="${cream}" ${ol(cream, 1.2)}/>` +
    `<rect x="-16" y="-14" width="5" height="28" rx="2.5" fill="${shade(cream, 0.15)}" ${ol(cream, 1)}/><rect x="11" y="-14" width="5" height="28" rx="2.5" fill="${shade(cream, 0.15)}" ${ol(cream, 1)}/>` +
    `<path d="M-8 -5C-4 -8 -2 2 2 0C5 -2 4 2 5 4" fill="none" stroke="${P.red}" stroke-width="1.4" stroke-dasharray="2 2"/>` +
    `<path d="M3 2L9 8M9 2L3 8" stroke="${P.red}" stroke-width="2.4" stroke-linecap="round"/>`;
  const shell =
    `<path d="M0 14L-15 -2Q-16 -10 -8 -12Q-4 -18 0 -17Q4 -18 8 -12Q16 -10 15 -2Z" fill="url(#${p}sh)" ${ol(P.pink, 1.4)}/>` +
    `<path d="M0 13L-9 -9M0 13V-15M0 13L9 -9" stroke="${edge(P.pink, P)}" stroke-opacity=".5" stroke-width="1.2"/>`;
  const anchor =
    `<circle cx="0" cy="-13" r="3.5" fill="none" stroke="${P.blue}" stroke-width="2.6"/>` +
    `<path d="M0 -9V13M-8 -5H8M-12 2Q-10 13 0 13Q10 13 12 2" fill="none" stroke="${P.dark ? mix(P.blue, P.sky, 0.3) : P.blue}" stroke-width="3.4" stroke-linecap="round"/>` +
    `<path d="M-15 4L-12 -1L-8 4ZM15 4L12 -1L8 4Z" fill="${P.blue}"/>`;

  /** ship lantern hanging from a bracket at (x, y) */
  const lantern = (x: number, y: number, s: number) =>
    g(
      x,
      y,
      s,
      `<circle cx="0" cy="14" r="34" fill="url(#${p}lh)"/>` +
        `<path d="M0 -6V0" stroke="${edge(gold, P)}" stroke-width="2"/><circle cx="0" cy="-8" r="3" fill="none" stroke="${gold}" stroke-width="1.6"/>` +
        `<path d="M-8 0H8L10 5H-10Z" fill="url(#${p}gd)" ${ol(gold, 1.2)}/>` +
        `<rect x="-9" y="5" width="18" height="20" rx="3" fill="url(#${p}lg)" ${ol(gold, 1.4)}/>` +
        `<path d="M-3 5V25M3 5V25" stroke="${gold}" stroke-width="1.2"/>` +
        `<path d="M-10 25H10L8 30H-8Z" fill="url(#${p}gd)" ${ol(gold, 1.2)}/>`,
    );

  /** ship's wheel: rim, 8 spokes with handles, brass hub */
  let spokes = '';
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4;
    spokes += `M${n(7 * Math.cos(a))} ${n(7 * Math.sin(a))}L${n(30 * Math.cos(a))} ${n(30 * Math.sin(a))}`;
  }
  const wheel = g(
    420,
    74,
    1,
    `<path d="${spokes}" stroke="${edge(wood, P)}" stroke-width="7" stroke-linecap="round"/>` +
      `<path d="${spokes}" stroke="url(#${p}wd)" stroke-width="4.4" stroke-linecap="round"/>` +
      `<circle cx="0" cy="0" r="20" fill="none" stroke="${edge(wood, P)}" stroke-width="8"/>` +
      `<circle cx="0" cy="0" r="20" fill="none" stroke="url(#${p}wd)" stroke-width="5"/>` +
      `<circle cx="0" cy="0" r="7" fill="url(#${p}gb)" ${ol(gold, 1.4)}/>`,
  );

  /** brass corner plate with rivets */
  const corner = (x: number, y: number, sx: number, sy: number) =>
    `<path transform="translate(${x} ${y}) scale(${sx} ${sy})" d="M0 0H22L0 22Z" fill="url(#${p}gd)" ${ol(gold, 1.2)}/>`;
  const rivets = [324, 516]
    .flatMap((x) => [176, 268, 330].map((y) => `<circle cx="${x}" cy="${y}" r="2.4" fill="url(#${p}gb)"/>`))
    .join('');

  /** stars (dark) / gulls (light) in the sky, right of the calm third */
  const r = rng(11);
  let sky = '';
  if (P.dark) {
    for (let i = 0; i < 16; i++)
      sky += `<circle cx="${n(240 + r() * 390)}" cy="${n(14 + r() * 120)}" r="${n(0.6 + r() * 1.2)}" fill="#FFFFFF" fill-opacity="${n(0.4 + r() * 0.5)}"/>`;
  } else {
    sky += [
      [262, 70, 1],
      [288, 56, 0.8],
      [560, 112, 0.9],
    ]
      .map(
        ([x, y, s]) =>
          `<path d="M${x} ${y}q${n(6 * s!)} ${n(-6 * s!)} ${n(12 * s!)} 0q${n(6 * s!)} ${n(-6 * s!)} ${n(12 * s!)} 0" fill="none" stroke="${shade(P.sky, 0.5)}" stroke-opacity=".6" stroke-width="2" stroke-linecap="round"/>`,
      )
      .join('');
  }

  const defs =
    `<defs>${bg.defs}` +
    body(`${p}wd`, wood, 0.3, 0.35, 0.7, 1) +
    body(`${p}gd`, gold, 0.5, 0.35, 0.5, 1) +
    ball(`${p}gb`, gold, 0.6, 0.3) +
    ball(`${p}lg`, lamp, 0.5, 0.15) +
    fade(`${p}lh`, P.dark ? lamp : P.orange, P.dark ? 0.55 : 0.3) +
    fade(`${p}mn`, P.dark ? '#FFF1C2' : '#FFE7A3', P.dark ? 0.5 : 0.8) +
    ball(`${p}kn`, shade(mix(P.orange, P.red, 0.3), 0.15), 0.5, 0.4) +
    body(`${p}sh`, mix(P.orange, P.pink, 0.45), 0.45, 0.3, 0.4, 1) +
    `<linearGradient id="${p}sea" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${tint(sea, 0.15)}"/><stop offset="1" stop-color="${shade(sea, 0.25)}"/></linearGradient>` +
    fade(`${p}sd`, P.dark ? '#000000' : shade(P.ink, 0.2), P.dark ? 0.6 : 0.22) +
    // the lighthouse beam: warm light fading out to sea (a flat translucent wedge read grey on the night sky)
    `<linearGradient id="${p}bm" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${P.dark ? '#FFE27A' : lamp}"/><stop offset=".55" stop-color="${P.dark ? '#FFD45C' : lamp}" stop-opacity="${P.dark ? 0.75 : 0.55}"/><stop offset="1" stop-color="${P.dark ? '#FFD45C' : lamp}" stop-opacity="0"/></linearGradient>` +
    `</defs>`;

  // moon / sun: up and left of the lever handle (its knob reaches y ≈ 110 at x 560–590 and hid the lower part of the
  // moon on the lobby card), right of the ship's wheel (x 390–450)
  const sunX = 515;
  const sunY = 46;
  const reels = [shell, map, chest, map, anchor];

  return (
    defs +
    bg.body +
    sky +
    // moon / sun
    `<circle cx="${sunX}" cy="${sunY}" r="54" fill="url(#${p}mn)"/>` +
    `<circle cx="${sunX}" cy="${sunY}" r="20" fill="${P.dark ? '#FFF1C2' : '#FFD166'}" fill-opacity="${P.dark ? 0.9 : 1}"/>` +
    (P.dark ? `<circle cx="${sunX + 8}" cy="${sunY - 6}" r="17" fill="${top}"/>` : '') +
    // sea with a far rock, lighthouse and a sail
    `<rect y="236" width="640" height="140" fill="url(#${p}sea)"/>` +
    `<path d="M0 236H640" stroke="#FFFFFF" stroke-opacity="${P.dark ? 0.15 : 0.6}" stroke-width="1.4"/>` +
    `<path d="M570 236Q590 214 612 216Q630 220 640 236Z" fill="${P.dark ? mix(P.bg, P.teal, 0.25) : mix(P.teal, P.green, 0.5)}"/>` +
    `<path d="M598 220L602 172H612L616 220Z" fill="${cream}" ${ol(cream, 1.2)}/>` +
    `<path d="M600 196H614M599 208H615" stroke="${P.red}" stroke-width="5"/>` +
    `<path d="M600 172L607 162L614 172Z" fill="${P.red}"/><rect x="603" y="164" width="8" height="8" fill="${lamp}"/>` +
    `<path d="M610 168L640 158V178Z" fill="url(#${p}bm)"/>` +
    `<path d="M248 232L250 196L268 228Z" fill="${cream}" fill-opacity=".85"/><path d="M244 232H272L268 238H248Z" fill="${plank}"/>` +
    `<path d="M230 258q8 -4 16 0t16 0M556 270q8 -4 16 0t16 0M240 300q8 -4 16 0t16 0M580 318q8 -4 16 0t16 0" fill="none" stroke="#FFFFFF" stroke-opacity="${P.dark ? 0.25 : 0.65}" stroke-width="2" stroke-linecap="round"/>` +
    // pier planks
    `<path d="M0 368H640V400H0Z" fill="url(#${p}wd)"/>` +
    `<path d="M0 368H640M0 384H640" stroke="${plank}" stroke-width="2"/>` +
    `<ellipse cx="420" cy="372" rx="138" ry="10" fill="url(#${p}sd)"/>` +
    // cabinet: crest + body of planks, rope along the edge, brass corners
    `<path d="M312 116Q312 98 330 98H510Q528 98 528 116V362Q528 372 518 372H322Q312 372 312 362Z" fill="url(#${p}wd)" ${ol(wood, 3)}/>` +
    `<path d="M313 140H527M313 252H527M313 300H527M313 338H527" stroke="${plank}" stroke-width="1.6" stroke-opacity=".8"/>` +
    `<path d="M316 116Q316 102 330 102H510Q524 102 524 116V362Q524 368 518 368H322Q316 368 316 362Z" fill="none" stroke="${shade(rope, 0.4)}" stroke-width="5"/>` +
    `<path d="M316 116Q316 102 330 102H510Q524 102 524 116V362Q524 368 518 368H322Q316 368 316 362Z" fill="none" stroke="${rope}" stroke-width="3.4" stroke-dasharray="4 2"/>` +
    corner(312, 98, 1, 1) +
    corner(528, 98, -1, 1) +
    corner(312, 372, 1, -1) +
    corner(528, 372, -1, -1) +
    rivets +
    [356, 388, 420, 452, 484]
      .map(
        (x) =>
          `<circle cx="${x}" cy="124" r="8" fill="url(#${p}lh)"/><circle cx="${x}" cy="124" r="4" fill="url(#${p}lg)" ${ol(gold, 1)}/>`,
      )
      .join('') +
    wheel +
    // reel window: brass frame, five cream reels, a payline
    `<rect x="326" y="150" width="188" height="92" rx="8" fill="${shade(wood, 0.55)}" stroke="url(#${p}gd)" stroke-width="5"/>` +
    [332, 368, 404, 440, 476]
      .map(
        (x) =>
          `<rect x="${x}" y="156" width="32" height="80" rx="4" fill="${cream}" stroke="${shade(cream, 0.25)}" stroke-width="1"/>`,
      )
      .join('') +
    reels.map((s, i) => g(348 + i * 36, 196, i === 2 ? 0.95 : 0.82, s)).join('') +
    `<path d="M330 196H510" stroke="${P.red}" stroke-opacity=".45" stroke-width="1.4"/>` +
    // lower panel with a brass wave scroll and a brass name plate
    `<rect x="334" y="262" width="172" height="64" rx="8" fill="${shade(wood, 0.2)}" stroke="${gold}" stroke-opacity=".8" stroke-width="2"/>` +
    `<path d="M352 300q10 -14 22 -4q8 6 14 -2M392 300q10 -14 22 -4q8 6 14 -2M432 300q10 -14 22 -4q8 6 14 -2" fill="none" stroke="${gold}" stroke-width="3" stroke-linecap="round"/>` +
    `<polygon points="${star(420, 282, 7, 1.8, 4)}" fill="${gold}"/>` +
    `<rect x="362" y="340" width="116" height="16" rx="6" fill="url(#${p}gd)" ${ol(gold, 1.6)}/>` +
    gloss(340, 196, 6, 40, 0, 0.12) +
    gloss(344, 116, 18, 4, -20, 0.3) +
    // lever: brass hub, arm, a turned wooden handle like a wheel spoke
    `<path d="M536 252L572 146" stroke="${edge(gold, P)}" stroke-width="11" stroke-linecap="round"/>` +
    `<path d="M536 252L572 146" stroke="url(#${p}gd)" stroke-width="7" stroke-linecap="round"/>` +
    `<rect x="526" y="236" width="18" height="34" rx="6" fill="url(#${p}gd)" ${ol(gold, 1.8)}/>` +
    `<g transform="translate(575 136) rotate(19)">` +
    `<path d="M-6 8C-10 2 -9 -6 -6 -12C-8 -18 -6 -24 0 -24C6 -24 8 -18 6 -12C9 -6 10 2 6 8Z" fill="url(#${p}kn)" ${ol(wood, 2)}/>` +
    `<rect x="-7" y="6" width="14" height="5" rx="2" fill="url(#${p}gd)" ${ol(gold, 1.2)}/>` +
    gloss(-3, -14, 2, 6, 0, 0.55) +
    `</g>` +
    lantern(306, 118, 1) +
    lantern(534, 118, 1)
  );
}
